import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, Briefcase, BarChart3, BookOpen, TrendingUp } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const tiers = [
  { bg: "bg-navy", text: "text-gold", label: "LEVEL 1", title: "Company Value / Core Competency", desc: "What your company stands for. Your values become the foundation of every role framework.", example: "Think Ahead \u2192 Customer First \u2192 Act with Integrity" },
  { bg: "bg-gold", text: "text-navy", label: "LEVEL 2", title: "Role Competency / Main Skill", desc: "Skill + Knowledge + Ability required for this role. The higher-order capabilities that define excellence.", example: "Strategic Thinking \u2192 Analytical Reasoning \u2192 Commercial Acumen" },
  { bg: "bg-white border-2 border-navy", text: "text-navy", label: "LEVEL 3", title: "Observable Sub-Skills", desc: "Specific actions that can be seen, measured, and assessed on the job. The evidence of competence.", example: "Analyses competitor data to identify positioning gaps \u2192 Conducts structured performance reviews" },
];

const useCases = [
  { icon: Briefcase, title: "Hiring & Recruitment", desc: "Design structured interview questions and assessment criteria directly from observable skills" },
  { icon: BarChart3, title: "Performance Reviews", desc: "Assess employees against specific observable behaviours \u2014 not vague ratings" },
  { icon: BookOpen, title: "Learning & Development", desc: "Identify skill gaps and build targeted learning journeys for every role" },
  { icon: TrendingUp, title: "Succession Planning", desc: "Map readiness for next level roles using the same framework consistently" },
];

const faqs = [
  { q: "What is a competency framework and why does it matter?", a: "A competency framework defines what great performance looks like for each role \u2014 the skills, knowledge, and behaviours required to succeed. It aligns your hiring, performance reviews, and learning programmes to the same standard." },
  { q: "How does RoleIQ use AI to generate competencies?", a: "RoleIQ uses Claude \u2014 Anthropic\u2019s enterprise AI \u2014 to analyse your company values, industry, role, and level, then generate a structured 3-tier framework of competencies, KPIs, and observable skills specific to your context." },
  { q: "Can I use my own company values and culture?", a: "Yes \u2014 your company values become the Core Competency domains. RoleIQ builds the entire framework around your specific values, not generic templates." },
  { q: "What is the 3-tier SKA model?", a: "SKA stands for Skill, Knowledge, and Ability. Level 1 is your company value. Level 2 is the role competency (the SKA cluster). Level 3 is the observable sub-skill \u2014 what you can actually see and measure." },
  { q: "What format is the Excel output in?", a: "A premium formatted Excel workbook with two sheets \u2014 an Instructions sheet with colour legend, and a Competency Framework sheet with columns for Company Value, Role Competency, Observable Skill, Fits Current Role, Fits Future Role, Priority, and Leader Notes." },
  { q: "Can I edit the generated output?", a: "Yes \u2014 both the Excel and Job Description outputs are fully editable. The Excel has blank target fields for your team to fill in KPI numbers." },
  { q: "Is RoleIQ suitable for all industries and company sizes?", a: "Yes \u2014 RoleIQ works for any industry, any company size, and any role from entry level to CXO. It has been designed for the Indian market but works globally." },
  { q: "How is RoleIQ different from generic HR templates?", a: "Generic templates are one-size-fits-all. RoleIQ generates frameworks specific to your company values, your industry, your role, and your level \u2014 in seconds. No consultant fees, no weeks of workshops." },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-navy/10">
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center py-5 text-left">
        <span className="font-dm text-navy font-semibold pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 text-gold flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="font-dm text-navy/70 text-sm pb-5 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function HowItWorksPage() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-navy pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
            From Company Values to Observable Skills — in 30 Seconds
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-dm text-white/70 text-lg max-w-xl mx-auto">
            The world's first 3-tier AI competency framework builder — aligned to your culture, built for every industry
          </motion.p>
        </div>
      </section>

      {/* 3-Tier Model */}
      <section className="bg-cream py-20">
        <div className="max-w-3xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-14">
            The RoleIQ Framework — Three Levels of Intelligence
          </motion.h2>
          <div className="space-y-6">
            {tiers.map((t, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.2 }}>
                <div className={`${t.bg} ${t.text} rounded-xl p-8`}>
                  <span className="text-xs font-dm uppercase tracking-widest opacity-70">{t.label}</span>
                  <h3 className="font-playfair text-2xl font-bold mt-1 mb-3">{t.title}</h3>
                  <p className="font-dm text-sm opacity-80 mb-4">{t.desc}</p>
                  <p className="font-dm text-xs opacity-60 italic">Example: {t.example}</p>
                </div>
                {i < 2 && <div className="flex justify-center py-2"><ChevronDown className="w-6 h-6 text-gold" /></div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-12">
            One Framework. Four Powerful Applications.
          </motion.h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {useCases.map((uc, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white border-l-4 border-gold rounded-xl shadow-md p-6 hover:-translate-y-1 transition-transform"
              >
                <uc.icon className="w-8 h-8 text-gold mb-3" />
                <h3 className="font-playfair text-navy text-lg font-semibold mb-2">{uc.title}</h3>
                <p className="font-dm text-navy/60 text-sm">{uc.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Output */}
      <section className="bg-cream py-20">
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-12">
            What your output looks like
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: "Competency Framework Preview", rows: ["Company Value: Innovation", "Role Competency: Strategic Thinking", "Definition: Ability to think long-term...", "KPI: Strategic initiative completion (Target: ___)", "Observable Skill: Analyses market data to identify emerging trends and recommend proactive business pivots"] },
              { title: "Job Description Preview", rows: ["ACME CORP \u2014 JOB DESCRIPTION", "Role: Senior Product Manager", "Department: Product Development", "Required Competencies (SKA Framework):", "Innovation \u2192 Strategic Thinking", "Qualifications: ___________"] },
            ].map((card, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="bg-white rounded-xl shadow-lg p-6 hover:scale-[1.02] transition-transform"
              >
                <div className="bg-navy text-gold font-dm text-sm font-semibold px-4 py-2 rounded-lg mb-4">{card.title}</div>
                <div className="space-y-2">
                  {card.rows.map((r, j) => (
                    <p key={j} className="font-dm text-navy/70 text-sm border-b border-navy/5 pb-2">{r}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-12">
            Frequently Asked Questions
          </motion.h2>
          <div>{faqs.map((faq, i) => <FAQItem key={i} {...faq} />)}</div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-white text-3xl font-bold mb-6">Ready to generate your first framework?</h2>
          <Link to="/generate" className="inline-block bg-gold text-navy font-dm font-bold px-10 py-4 rounded-lg hover:bg-gold/90 transition-colors">
            Generate Free — Takes 30 Seconds
          </Link>
        </div>
      </section>
    </div>
  );
}
